import { midrollSlotPlan } from './adConfigService.js';

function remainingBudget(startedAt, budgetMs) {
  return Math.max(0, Number(budgetMs || 0) - (Date.now() - startedAt));
}

function normalizeOutcome(value = {}) {
  const started = value?.started === true;
  return {
    attempted: value?.attempted !== false,
    started,
    completed: started && value?.completed === true,
    skipped: !started && value?.skipped === true,
    consumed: started || value?.consumed === true,
    source: String(value?.source || ''),
    code: value?.code || null,
    durationSec: Number.isFinite(Number(value?.durationSec)) ? Number(value.durationSec) : null,
  };
}

export async function runFixedMidrollPod({
  controller,
  config,
  titleId,
  contentDuration = 0,
  contentTitle = '',
  sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
} = {}) {
  if (!controller || !config) {
    return {
      delivered: false,
      fulfilled: false,
      attempted: 0,
      attemptedRequests: 0,
      started: 0,
      completed: 0,
      skipped: 0,
      slots: [],
      podSeed: 0,
      elapsedMs: 0,
      budgetMs: Number(config?.maxPodMs || 0),
    };
  }

  const podSeed = Date.now();
  const slots = midrollSlotPlan({ config, titleId, podSeed });
  const startedAt = Date.now();
  const budgetMs = Math.max(1000, Number(config.maxPodMs) || 380000);
  const results = [];

  for (const slot of slots) {
    let remainingMs = remainingBudget(startedAt, budgetMs);
    if (remainingMs <= 0) break;

    const result = {
      id: slot.id,
      index: slot.index,
      slotNumber: slot.slotNumber || slot.index + 1,
      owner: slot.owner,
      podSeed,
      attempted: false,
      attemptCount: 0,
      started: false,
      completed: false,
      skipped: false,
      consumed: false,
      source: '',
      code: null,
      candidates: [...slot.candidates],
      errors: [],
    };

    if (!slot.candidates.length) {
      result.skipped = true;
      result.code = 'WATCHVIM_SLOT_NO_INVENTORY';
      results.push(result);
      continue;
    }

    // Each explicit position owns its complete source waterfall. A candidate
    // may advance only if no creative ever STARTED/IMPRESSION. Once a viewer
    // sees an ad, that position is consumed even if a later lifecycle error
    // occurs, and the pod proceeds to the next position rather than doubling it.
    for (const tagUrl of slot.candidates) {
      remainingMs = remainingBudget(startedAt, budgetMs);
      if (remainingMs <= 0) break;
      result.attempted = true;
      result.attemptCount += 1;

      try {
        const outcome = normalizeOutcome(await controller.playTag(tagUrl, {
          muted: config.startMuted,
          loadTimeoutMs: Math.min(config.vastLoadTimeoutMs, Math.max(1000, remainingMs)),
          maxAdMs: config.maxAdMs,
          requestBudgetMs: remainingMs,
          contentDuration: Number(contentDuration) || 0,
          contentTitle,
          position: 'midroll',
          slotId: slot.id,
          slotNumber: result.slotNumber,
          slotTotal: slots.length,
        }));

        result.started = outcome.started;
        result.completed = outcome.completed;
        result.skipped = outcome.skipped;
        result.consumed = outcome.consumed;
        result.code = outcome.code;
        result.durationSec = outcome.durationSec;
        result.source = outcome.source;

        if (outcome.started || outcome.consumed) break;
      } catch (error) {
        result.errors.push({
          code: error?.code || null,
          message: String(error?.message || error || 'Ad request failed'),
        });
      }
    }

    if (!result.started) {
      result.skipped = true;
      result.consumed = false;
      if (!result.code) result.code = result.errors.at(-1)?.code || 'WATCHVIM_MIDROLL_SLOT_SKIPPED';
    }
    results.push(result);

    if (slot.index < slots.length - 1 && Number(config.midrollPodGapMs) > 0) {
      const remainingAfterSlot = remainingBudget(startedAt, budgetMs);
      if (remainingAfterSlot > 0) {
        await sleep(Math.min(Number(config.midrollPodGapMs), remainingAfterSlot));
      }
    }
  }

  const attempted = results.filter((slot) => slot.attempted).length;
  const attemptedRequests = results.reduce((sum, slot) => sum + Number(slot.attemptCount || 0), 0);
  const started = results.filter((slot) => slot.started).length;
  const completed = results.filter((slot) => slot.completed).length;
  const skipped = results.filter((slot) => slot.skipped).length;

  return {
    delivered: started > 0,
    fulfilled: started === slots.length,
    requested: slots.length,
    attempted,
    attemptedRequests,
    started,
    completed,
    skipped,
    count: started,
    slots: results,
    podSeed,
    elapsedMs: Date.now() - startedAt,
    budgetMs,
  };
}

